/**
 * Unified DataSources Service
 * API Version: V2
 * Endpoint: /api/v2/datasources
 *
 * Replaces:
 * - accountCompetitorsService
 * - accountPartnersService
 * - accountInfluencersService (future)
 * - mainChannelService (future)
 */

import { httpClient } from '@/services/httpClient';
import type {
  BackendDataSource,
  BackendDataSourcesPaginatedResponse,
  DataSource,
  DataSourcesFilters,
  DataSourcesPaginatedResponse,
  GroupedDataSourcesPaginatedResponse,
  CreateDataSourceRequest,
  UpdateDataSourceRequest,
} from '@/types/dataSources';
import {
  adaptBackendDataSource,
  adaptBackendPaginatedResponse,
  adaptBackendPaginatedResponseGrouped,
  getTypeCodeFromFilter,
  DATA_SOURCE_TYPE_CODES,
} from '@/types/dataSources';

class DataSourcesService {
  private readonly baseUrl = '/api/v2/datasources';

  /**
   * Get datasources with filters (paginated)
   * Endpoint: GET /api/v2/datasources?type=X&account_id=Y
   *
   * @param filters - Filter options
   * @returns Promise<DataSourcesPaginatedResponse>
   */
  async getList(filters: DataSourcesFilters = {}): Promise<DataSourcesPaginatedResponse> {
    try {
      const params: Record<string, string | number> = {};

      // Account ID (required in most cases)
      if (filters?.account_id) {
        params.account_id = filters.account_id;
      }

      // Type filter (convert string to number)
      if (filters?.type) {
        const typeCode = DATA_SOURCE_TYPE_CODES[filters.type];
        params.type = typeCode;
      }

      // Platform filter - not supported by backend yet, will filter client-side
      // Backend groups by datasource, not by platform

      // Is Active filter
      if (filters?.is_active !== undefined) {
        params.is_active = filters.is_active ? '1' : '0';
      }

      // Search
      if (filters?.search) {
        params.search = filters.search;
      }

      // Pagination
      if (filters?.page) {
        params.page = filters.page;
      }
      if (filters?.per_page) {
        params.per_page = filters.per_page;
      }

      const response = await httpClient.get<BackendDataSourcesPaginatedResponse>(
        this.baseUrl,
        { params }
      );

      // Adapt backend response to UI format
      const adapted = adaptBackendPaginatedResponse(response);
      return adapted;
    } catch (error) {
      console.error('[DataSourcesService] Error fetching datasources:', error);
      throw error;
    }
  }

  /**
   * Get datasources with filters (paginated, grouped format)
   * Same as getList but returns grouped datasources
   * Endpoint: GET /api/v2/datasources?type=X&account_id=Y
   *
   * @param filters - Filter options
   * @returns Promise<GroupedDataSourcesPaginatedResponse>
   */
  async getListGrouped(filters: DataSourcesFilters = {}): Promise<GroupedDataSourcesPaginatedResponse> {
    try {
      const params: Record<string, string | number> = {};

      // Account ID (required in most cases)
      if (filters?.account_id) {
        params.account_id = filters.account_id;
      }

      // Type filter (convert string to number)
      if (filters?.type) {
        const typeCode = DATA_SOURCE_TYPE_CODES[filters.type];
        params.type = typeCode;
      }

      // Is Active filter
      if (filters?.is_active !== undefined) {
        params.is_active = filters.is_active ? '1' : '0';
      }

      // Search
      if (filters?.search) {
        params.search = filters.search;
      }

      // Pagination
      if (filters?.page) {
        params.page = filters.page;
      }
      if (filters?.per_page) {
        params.per_page = filters.per_page;
      }

      const response = await httpClient.get<BackendDataSourcesPaginatedResponse>(
        this.baseUrl,
        { params }
      );

      const adapted = adaptBackendPaginatedResponseGrouped(response);
      return adapted;
    } catch (error) {
      console.error('[DataSourcesService.getListGrouped] Error:', error);
      throw error;
    }
  }

  /**
   * Get a specific datasource by ID
   * Endpoint: GET /api/v2/datasources/{id}
   *
   * @param id - DataSource ID
   * @returns Promise<DataSource[]> - Array because backend returns grouped format
   */
  async getDataSource(id: number): Promise<DataSource[]> {
    try {
      const url = `${this.baseUrl}/${id}`;
      const response = await httpClient.get<{ data: BackendDataSource }>(url);

      return adaptBackendDataSource(response.data);
    } catch (error) {
      console.error('[DataSourcesService] Error fetching datasource:', error);
      throw error;
    }
  }

  /**
   * Get a specific datasource by ID (raw backend format with dates)
   * Endpoint: GET /api/v2/datasources/{id}
   *
   * @param id - DataSource ID
   * @returns Promise<BackendDataSource> - Raw backend format with items and dates
   */
  async getDataSourceRaw(id: number): Promise<BackendDataSource> {
    try {
      const url = `${this.baseUrl}/${id}`;
      const response = await httpClient.get<{ data: BackendDataSource }>(url);

      return response.data;
    } catch (error) {
      console.error('[DataSourcesService] Error fetching datasource (raw):', error);
      throw error;
    }
  }

  /**
   * Create a new datasource with multiple platforms
   * Endpoint: POST /api/v2/datasources
   *
   * @param data - Create request with items array
   * @returns Promise<BackendDataSource>
   */
  async createDataSource(data: CreateDataSourceRequest): Promise<BackendDataSource> {
    try {
      const response = await httpClient.post<{ data: BackendDataSource }>(this.baseUrl, data);
      return response.data;
    } catch (error) {
      console.error('[DataSourcesService] Error creating datasource:', error);
      throw error;
    }
  }

  /**
   * Update a datasource (name, type, items, etc)
   * Endpoint: PUT /api/v2/datasources/{id}
   *
   * WARNING: This endpoint is DESTRUCTIVE!
   * Items not included in the items array will be DELETED.
   * Always GET the current datasource first to preserve existing items.
   *
   * @param datasourceId - Datasource ID
   * @param data - Update request (partial update)
   * @returns Promise<BackendDataSource>
   */
  async updateDataSource(
    datasourceId: number,
    data: UpdateDataSourceRequest
  ): Promise<BackendDataSource> {
    try {
      const response = await httpClient.put<{ data: BackendDataSource }>(
        `${this.baseUrl}/${datasourceId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('[DataSourcesService] Error updating datasource:', error);
      throw error;
    }
  }

  /**
   * Delete a datasource item (1 social network)
   * NOTE: API v2 doesn't have a DELETE items endpoint
   * Implementation: GET datasource → filter item → PUT with remaining items
   *
   * @param datasourceId - Datasource ID
   * @param itemId - Item ID to delete
   * @returns Promise<BackendDataSource>
   */
  async deleteDataSourceItem(datasourceId: number, itemId: number): Promise<BackendDataSource> {
    try {
      // 1. GET current datasource
      const currentDatasource = await httpClient.get<{ data: BackendDataSource }>(
        `${this.baseUrl}/${datasourceId}`
      );

      // 2. Filter out the item to be deleted and MAP COMPLETE STRUCTURE
      const remainingItems = currentDatasource.data.items
        .filter(item => item.id !== itemId)
        .map(item => ({
          id: item.id,
          // Preserve all fields to avoid backend validation errors
          author_id: item.author_id,
          network_id: item.social_network_id,  // Backend uses social_network_id in response
          dates: item.dates?.map(date => ({
            id: date.id,
            start_date: date.start_date,
            end_date: date.end_date,
            status: date.status
          })) || []
        }));

      // 3. PUT with remaining items (complete structure)
      return await this.updateDataSource(datasourceId, {
        items: remainingItems
      });
    } catch (error) {
      console.error('[DataSourcesService] Error deleting datasource item:', error);
      throw error;
    }
  }

  /**
   * Delete entire datasource (all items/platforms)
   */
  async deleteDataSource(datasourceId: number): Promise<void> {
    try {
      await httpClient.delete(`${this.baseUrl}/${datasourceId}`);
    } catch (error) {
      console.error('[DataSourcesService] Error deleting datasource:', error);
      throw error;
    }
  }

  /**
   * Helper: Get existing dates for a specific item in a datasource
   * Used to preserve all dates when updating
   *
   * @param datasourceId - Datasource ID
   * @param itemId - Item ID
   * @returns Array of existing dates
   */
  async getItemDates(datasourceId: number, itemId: number): Promise<Array<{
    id: number;
    start_date: string;
    end_date: string | null;
    status: 0 | 1 | 2 | 3;
  }>> {
    try {
      const response = await httpClient.get<{ data: BackendDataSource }>(
        `${this.baseUrl}/${datasourceId}`
      );

      const item = response.data.items.find(item => item.id === itemId);

      if (!item?.dates) {
        return [];
      }

      return item.dates.map(d => ({
        id: d.id,
        start_date: d.start_date,
        end_date: d.end_date,
        status: d.status,
      }));
    } catch (error) {
      console.error('[DataSourcesService] Error fetching item dates:', error);
      return [];
    }
  }

  /**
   * Get datasources (backend format) - For custom adapters
   * Returns backend structure without adapting to UI format
   *
   * @param params - Query parameters
   * @returns Promise<BackendDataSourcesPaginatedResponse>
   *
   * @example
   * ```ts
   * const response = await dataSourcesService.getDataSources({
   *   account_id: 123,
   *   format: 'compact',
   *   per_page: 100
   * });
   * ```
   */
  async getDataSources(params?: {
    account_id?: number;
    format?: 'compact' | 'full';
    page?: number;
    per_page?: number;
  }): Promise<BackendDataSourcesPaginatedResponse> {
    try {
      const searchParams: Record<string, string | number> = {};

      if (params?.account_id) {
        searchParams.account_id = params.account_id;
      }

      if (params?.format) {
        searchParams.format = params.format;
      }

      if (params?.page) {
        searchParams.page = params.page;
      }

      if (params?.per_page) {
        searchParams.per_page = params.per_page;
      }

      const response = await httpClient.get<BackendDataSourcesPaginatedResponse>(
        this.baseUrl,
        { params: searchParams }
      );
      return response;
    } catch (error) {
      console.error('[DataSourcesService] Error fetching datasources:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const dataSourcesService = new DataSourcesService();
export default dataSourcesService;
